import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import "../fonts/breuer-headline.css";
import "./stage1Escape.css";
import { fireGameStagePopup } from "../../functions/gameStagePopup";
import Stage1TaskTriage from "./Stage1TaskTriage";
import Stage1DeskOrganize from "./Stage1DeskOrganize";
import { STAGE1_PUZZLE_POINTS } from "./stage1TaskData";
import { STAGE1_DESK_PUZZLE_POINTS } from "./stage1DeskOrganizeData";
import { STAGE1_INBOX_PUZZLE_POINTS } from "./stage1InboxData";
import Stage1InboxTriage from "./Stage1InboxTriage";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  isLevelScoreRecorded,
  LEVELS_BY_ADVENTURE,
  markStageCompleted,
  saveGameStageAndReport,
  setStageActive,
  sumLevelScores,
} from "../../functions/stageReportSync";
import {
  showStageCompleteCelebration,
  navigateAfterCelebration,
} from "../../functions/stageCelebration";
import stage1Desk from "./img/stage1.png";
import stage1Mob from "./img/stage1_mob.png";
import screen2Desk from "./img/screen2.png";
import screen2Mob from "./img/screen2_mob.png";
import laptopImg from "./img/laptop.png";
import tabletImg from "./img/tablet.png";
import {
  applyStageBodyBackground,
  isMobViewport,
} from "./gameStageBackground";
import StageTimer from "./StageTimer";
import StageHelpButton from "./StageHelpButton";
import { fireEscapeHowToPlay } from "../../functions/escapeGrindHowToPlay";

const STAGE_NUM = 1;

const GAME_LEVEL = {
  TASK_TRIAGE: 1,
  DESK_ORGANIZE: 2,
  INBOX_TRIAGE: 3,
};

const STAGE1_STRESS = {
  START: 100,
  AFTER_LEVEL_1: 79,
  AFTER_LEVEL_2: 62,
  FINAL: 48,
};

function stressForStageRecord(stageRecord) {
  if (!stageRecord) return STAGE1_STRESS.START;
  if (isLevelScoreRecorded(stageRecord, GAME_LEVEL.INBOX_TRIAGE)) {
    return STAGE1_STRESS.FINAL;
  }
  if (isLevelScoreRecorded(stageRecord, GAME_LEVEL.DESK_ORGANIZE)) {
    return STAGE1_STRESS.AFTER_LEVEL_2;
  }
  if (isLevelScoreRecorded(stageRecord, GAME_LEVEL.TASK_TRIAGE)) {
    return STAGE1_STRESS.AFTER_LEVEL_1;
  }
  return STAGE1_STRESS.START;
}

const STAGE2_MEETING_ROOM_KEY = "stage2EnterMeetingRoom";

const PHASE = {
  LOADING: "loading",
  INTRO_STORY: "intro_story",
  NOTIFICATION: "notification",
  DESK_INTRO: "desk_intro",
  PUZZLE: "puzzle",
  TRIAGE_LEARNING: "triage_learning",
  PUZZLE2_INTRO: "puzzle2_intro",
  PUZZLE2: "puzzle2",
  ORG_LEARNING: "org_learning",
  PUZZLE3_INTRO: "puzzle3_intro",
  PUZZLE3: "puzzle3",
  INBOX_LEARNING: "inbox_learning",
  PROGRESS: "progress",
}; 

function pickBackgroundForPhase(phase) {
  const plainBgPhases = new Set([
    PHASE.PUZZLE2,
    PHASE.TRIAGE_LEARNING,
    PHASE.PUZZLE2_INTRO,
    PHASE.ORG_LEARNING,
    PHASE.PUZZLE3_INTRO,
    PHASE.INBOX_LEARNING,
    PHASE.PROGRESS,
  ]);
  if (plainBgPhases.has(phase)) return null;

  const mob = isMobViewport();
  const useDeskScene =
    phase === PHASE.DESK_INTRO ||
    phase === PHASE.PUZZLE ||
    phase === PHASE.PUZZLE3;
  if (useDeskScene) return mob ? screen2Mob : screen2Desk;
  return mob ? stage1Mob : stage1Desk;
}

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function StressMeter({ value, variant = "vertical", elevated = false }) {
  const isHorizontal = variant === "horizontal";

  return (
    <div
      className={`stage1-stress stage1-stress--${variant}${
        elevated ? " stage1-stress--elevated" : ""
      }`}
      role="meter"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Stress meter ${value} percent`}
    >
      <span className="stage1-stress__heading">Stress Meter</span>
      <div className="stage1-stress__track">
        <div
          className="stage1-stress__fill"
          style={
            isHorizontal ? { width: `${value}%` } : { height: `${value}%` }
          }
        />
      </div>
      <span className="stage1-stress__value">{value}%</span>
    </div>
  );
}

function ChaosCoffeeCupIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 7.5c.4-1.2 1.2-2 2.3-2.5" />
      <path d="M8 6c.4-1.2 1.2-2 2.3-2.5" />
      <path d="M11.5 6.5c.4-1.2 1.2-2 2.3-2.5" />
      <path d="M5 12h11.5a3.5 3.5 0 0 1 0 7H8.5A3.5 3.5 0 0 1 5 15.5V12z" />
      <path d="M16.5 12h1.8a2.2 2.2 0 0 0 0-4.4H16.5" />
    </svg>
  );
}

function ChaosStatCard({ tone, icon, value, label }) {
  return (
    <div className={`stage1-chaos-modal__stat stage1-chaos-modal__stat--${tone}`}>
      <span className="stage1-chaos-modal__stat-icon" aria-hidden>
        {icon}
      </span>
      <div className="stage1-chaos-modal__stat-body">
        <span className="stage1-chaos-modal__stat-value">{value}</span>
        <span className="stage1-chaos-modal__stat-label">{label}</span>
      </div>
    </div>
  );
}

function IntroStoryModal({ onContinue }) {
  return (
    <div
      className="stage1-modal-overlay stage1-chaos-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="intro-title"
    >
      <div className="stage1-chaos-modal">
        <header className="stage1-chaos-modal__header">
          <div className="stage1-chaos-modal__brand" aria-hidden>
            <span className="stage1-chaos-modal__brand-icon">
              <ChaosCoffeeCupIcon />
            </span>
          </div>
          <div className="stage1-chaos-modal__heading">
            <h2 id="intro-title" className="stage1-chaos-modal__title">
              Monday Morning Chaos <span aria-hidden>☀️</span>
            </h2>
            <p className="stage1-chaos-modal__time">
              <span className="stage1-chaos-modal__time-icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </span>
              9:00 AM
            </p>
          </div>
          <button
            type="button"
            className="stage1-chaos-modal__close"
            onClick={onContinue}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="stage1-chaos-modal__stats">
          <ChaosStatCard
            tone="emails"
            value="260"
            label="Unread Emails"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 6h16v12H4z" />
                <path d="m4 7 8 6 8-6" />
              </svg>
            }
          />
          <ChaosStatCard
            tone="meetings"
            value="14"
            label="Meetings Scheduled"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="5" width="18" height="16" rx="2" />
                <path d="M8 3v4M16 3v4M3 10h18" />
              </svg>
            }
          />
          <ChaosStatCard
            tone="tasks"
            value="23"
            label="Pending Tasks"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5h6a2 2 0 0 1 2 2v14H7V7a2 2 0 0 1 2-2z" />
                <path d="M9 3h6v4H9z" />
                <path d="M9 12h6M9 16h4" />
              </svg>
            }
          />
          <ChaosStatCard
            tone="breaks"
            value="0"
            label="Breaks Planned"
            icon={<ChaosCoffeeCupIcon />}
          />
        </div>

        <div className="stage1-chaos-modal__stress">
          <span className="stage1-chaos-modal__stress-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 18l4-8 4 4 4-10 4 14" />
            </svg>
          </span>
          <div>
            <p className="stage1-chaos-modal__stress-title">
              Your stress level is already rising.
            </p>
            <p className="stage1-chaos-modal__stress-text">
              Before you can even begin, notifications start pouring in from every
              direction.
            </p>
          </div>
        </div>

        <blockquote className="stage1-chaos-modal__quote">
          <p>
            You don&apos;t need to do everything. You need to focus on the right
            things.
          </p>
          <span className="stage1-chaos-modal__quote-plant" aria-hidden>
            🪴
          </span>
        </blockquote>

        <p className="stage1-chaos-modal__challenge">
          <span className="stage1-chaos-modal__challenge-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="9" />
              <circle cx="12" cy="12" r="5" />
              <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
            </svg>
          </span>
          Your challenge begins now.
        </p>

        <button type="button" className="stage1-chaos-modal__cta" onClick={onContinue}>
          <span className="stage1-chaos-modal__cta-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="9" />
              <circle cx="12" cy="12" r="5" />
              <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
            </svg>
          </span>
          Take Control
          <span className="stage1-chaos-modal__cta-chevron" aria-hidden>
            ›
          </span>
        </button>
      </div>
    </div>
  );
}

function OrgLearningModal({ onContinue }) {
  return (
    <div className="stage1-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="org-title">
      <div className="stage1-modal">
        <h2 id="org-title" className="stage1-modal__title">
          Why Organization Matters
        </h2>
        <p className="stage1-modal__time">Learning Moment</p>
        <div className="stage1-modal__body">
          <p>
            A cluttered workspace competes for your attention. Every loose paper,
            misplaced mug, and stray cable pulls focus away from what you&apos;re
            trying to accomplish.
          </p>
          <p>
            When your environment is organized, your brain spends less energy
            searching — and more energy doing meaningful work.
          </p>
          <p className="stage1-modal__takeaway">
            Key takeaway: Less clutter. More clarity.
          </p>
        </div>
        <button type="button" className="stage1-modal__cta" onClick={onContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}

function Puzzle3IntroModal({ onContinue }) {
  return (
    <div className="stage1-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="inbox-title">
      <div className="stage1-modal">
        <h2 id="inbox-title" className="stage1-modal__title">
          Inbox Avalanche
        </h2>
        <p className="stage1-modal__time">Level 3 — Your Inbox</p>
        <div className="stage1-modal__body">
          <p>
            <strong>260 unread emails.</strong> Notifications, newsletters, meeting
            invites, and &ldquo;urgent&rdquo; messages all demand your attention at once.
          </p>
          <p>
            Not every message needs a response right now — some need action, some
            can wait, and some are just noise. Sort the signal from the clutter
            before it derails your day.
          </p>
        </div>
        <button type="button" className="stage1-modal__cta" onClick={onContinue}>
          Sort the Noise
        </button>
      </div>
    </div>
  );
}

function InboxLearningModal({ onContinue }) {
  return (
    <div className="stage1-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="inbox-learn-title">
      <div className="stage1-modal">
        <h2 id="inbox-learn-title" className="stage1-modal__title">
          Inbox Triage
        </h2>
        <p className="stage1-modal__time">Learning Moment</p>
        <div className="stage1-modal__body">
          <p>
            Your inbox is a stream, not a to-do list. Treating every message as
            equally urgent is how reactive work takes over your day.
          </p>
          <p>
            Sort by what truly needs your attention now — respond when it matters,
            archive what&apos;s useful, and let the rest go.
          </p>
          <p className="stage1-modal__takeaway">
            Key takeaway: Not every message deserves immediate attention.
          </p>
        </div>
        <button type="button" className="stage1-modal__cta" onClick={onContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}

function ProgressWinItem({ icon, children, tone = "success" }) {
  return (
    <li className={`stage1-modal__win stage1-modal__win--${tone}`}>
      <span className="stage1-modal__win-icon" aria-hidden>
        {icon}
      </span>
      <span className="stage1-modal__win-text">{children}</span>
    </li>
  );
}

function ProgressModal({ onContinue }) {
  return (
    <div className="stage1-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="progress-title">
      <div className="stage1-modal stage1-modal--progress">
        <div className="stage1-modal__hero" aria-hidden>
          <span className="stage1-modal__hero-ring" />
          <span className="stage1-modal__hero-icon">✓</span>
        </div>
        <h2 id="progress-title" className="stage1-modal__title">
          Progress
        </h2>
        <p className="stage1-modal__badge">Stage 1 Complete</p>
        <ul className="stage1-modal__wins">
          <ProgressWinItem icon="🎯" tone="success">
            Your priorities are clear.
          </ProgressWinItem>
          <ProgressWinItem icon="🗂️" tone="success">
            Your workspace is organized.
          </ProgressWinItem>
          <ProgressWinItem icon="📬" tone="success">
            Your inbox is under control.
          </ProgressWinItem>
          <ProgressWinItem icon="📉" tone="calm">
            The stress meter begins to settle.
          </ProgressWinItem>
          <ProgressWinItem icon="⚡" tone="warn">
            But one major challenge remains.
          </ProgressWinItem>
        </ul>
        <div className="stage1-modal__quote stage1-modal__quote--calendar">
          <span className="stage1-modal__quote-icon" aria-hidden>
            📅
          </span>
          <p>Your calendar — and the meetings waiting inside it.</p>
        </div>
        <button type="button" className="stage1-modal__cta stage1-modal__cta--arrow" onClick={onContinue}>
          <span>Enter Meeting Room</span>
          <span className="stage1-modal__cta-icon" aria-hidden>
            →
          </span>
        </button>
      </div>
    </div>
  );
}

function TriageLearningModal({ onContinue }) {
  return (
    <div className="stage1-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="triage-title">
      <div className="stage1-modal">
        <h2 id="triage-title" className="stage1-modal__title">
          The Triage Method
        </h2>
        <p className="stage1-modal__time">Learning Moment</p>
        <div className="stage1-modal__body">
          <p>
            Triage means sorting what&apos;s in front of you by impact — not by how
            loud or urgent it feels. Some tasks look critical but barely move the
            needle; others are quiet yet change everything.
          </p>
          <p>
            Before you dive into activity, ask: <em>What actually matters today?</em>{" "}
            Rank by outcome, then work from the top down.
          </p>
          <p className="stage1-modal__takeaway">
            Key takeaway: Focus on impact before activity.
          </p>
        </div>
        <button type="button" className="stage1-modal__cta" onClick={onContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}

function Puzzle2IntroModal({ onContinue }) {
  return (
    <div className="stage1-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="access-card-title">
      <div className="stage1-modal">
        <h2 id="access-card-title" className="stage1-modal__title">
          The Missing Access Card
        </h2>
        <p className="stage1-modal__time">Level 2 — Your Desk</p>
        <div className="stage1-modal__body">
          <p>
            Your desk is buried under papers, mugs, and half-finished tasks. Somewhere
            in the clutter is your <strong>Meeting Room Access Card</strong> — and
            you need it for your next meeting.
          </p>
          <p>
            You can&apos;t find it until the workspace is organized. Put every item back
            where it belongs, and the card should turn up.
          </p>
        </div>
        <button type="button" className="stage1-modal__cta" onClick={onContinue}>
          Organize Your Workspace
        </button>
      </div>
    </div>
  );
}

function DeskIntroModal({ onContinue }) {
  return (
    <div className="stage1-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="desk-title">
      <div className="stage1-modal">
        <h2 id="desk-title" className="stage1-modal__title">
          Everything Feels Urgent
        </h2>
        <p className="stage1-modal__time">Level 1 — Your Desk</p>
        <div className="stage1-modal__body">
          <p>
            Every task on your list looks important. Emails keep piling up, meetings
            fill your calendar, and deadlines are closing in.
          </p>
          <p>
            The real skill isn&apos;t doing more — it&apos;s identifying what
            deserves your attention first. Prioritize your day before the grind takes
            over.
          </p>
        </div>
        <button type="button" className="stage1-modal__cta" onClick={onContinue}>
          Prioritize Your Day
        </button>
      </div>
    </div>
  );
}

function NotificationBlink({ onClick }) {
  return (
    <div className="stage1-desk-scene" aria-hidden={false}>
      <div className="stage1-notification-wrap stage1-notification-wrap--laptop">
        <button type="button" className="stage1-notification" onClick={onClick}>
          <span className="stage1-notification__icon" aria-hidden>
            🔔
          </span>
          <span className="stage1-notification__text">New message — tap to read</span>
        </button>
      </div>
    </div>
  );
}

export default function Stage1() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  const [phase, setPhase] = useState(PHASE.LOADING);
  const [stressLevel, setStressLevel] = useState(STAGE1_STRESS.START);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage1Points, setStage1Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [isMobilePortrait, setIsMobilePortrait] = useState(isMobViewport);

  useEffect(() => {
    const syncViewport = () => setIsMobilePortrait(isMobViewport());
    syncViewport();
    window.addEventListener("resize", syncViewport);
    window.addEventListener("orientationchange", syncViewport);
    return () => {
      window.removeEventListener("resize", syncViewport);
      window.removeEventListener("orientationchange", syncViewport);
    };
  }, []);

  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const demoRedirectTriggeredRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const showStressMeter =
    phase === PHASE.NOTIFICATION ||
    phase === PHASE.DESK_INTRO ||
    phase === PHASE.PUZZLE ||
    phase === PHASE.PUZZLE2 ||
    phase === PHASE.PUZZLE3 ||
    phase === PHASE.PROGRESS;

  const isPuzzlePhase =
    phase === PHASE.PUZZLE || phase === PHASE.PUZZLE2 || phase === PHASE.PUZZLE3;

  useEffect(() => {
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [themeStatus, dispatch]);

  useEffect(() => {
    document.body.classList.add("stage1-escape-page");
    const lockPuzzleScroll = phase === PHASE.PUZZLE || phase === PHASE.PUZZLE3;
    if (lockPuzzleScroll) {
      document.body.classList.add("stage1-puzzle-active");
    } else {
      document.body.classList.remove("stage1-puzzle-active");
    }

    const html = document.documentElement;
    const body = document.body;
    const bgKeys = [
      "background-image",
      "background-size",
      "background-position",
      "background-repeat",
      "background-attachment",
      "background-color",
      "min-height",
    ];
    const bodySnapshot = bgKeys.map((k) => [k, body.style.getPropertyValue(k)]);
    const htmlMinHeight = html.style.minHeight;

    const applyBackground = () => {
      const bg = pickBackgroundForPhase(phase);
      applyStageBodyBackground(body, {
        foregroundUrl: bg,
        mob: isMobViewport(),
        plainPhase: !bg,
      });
    };

    html.style.minHeight = "100%";
    body.style.minHeight = "100dvh";
    applyBackground();
    window.addEventListener("resize", applyBackground);
    window.addEventListener("orientationchange", applyBackground);

    return () => {
      window.removeEventListener("resize", applyBackground);
      window.removeEventListener("orientationchange", applyBackground);
      bodySnapshot.forEach(([k, v]) => {
        if (v) body.style.setProperty(k, v);
        else body.style.removeProperty(k);
      });
      if (htmlMinHeight) html.style.minHeight = htmlMinHeight;
      else html.style.removeProperty("min-height");
      document.body.classList.remove("stage1-escape-page");
      document.body.classList.remove("stage1-puzzle-active");
    };
  }, [phase]);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;
    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();
    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);
    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage1Points);
  }, [stage1Points]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) {
          setDbReady(true);
          setPhase(PHASE.INTRO_STORY);
        }
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) {
          setDbReady(true);
          setPhase(PHASE.INTRO_STORY);
        }
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);

        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: storedUser.name || "",
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (!rowId) {
          if (!cancelled) {
            setDbReady(true);
            setPhase(PHASE.INTRO_STORY);
          }
          return;
        }

        if (!cancelled) setStageRowId(rowId);

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));

        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage1Sum = sumLevelScores(st, LEVELS_BY_ADVENTURE[STAGE_NUM]);

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);
          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage1Sum);
          setStage1Points(dbStage1Sum);
          setDisplayTotalScore(dbTotal);

          let resumePhase = PHASE.INTRO_STORY;
          if (isLevelScoreRecorded(st, GAME_LEVEL.DESK_ORGANIZE)) {
            resumePhase = PHASE.PUZZLE3_INTRO;
          } else if (isLevelScoreRecorded(st, GAME_LEVEL.TASK_TRIAGE)) {
            resumePhase = PHASE.PUZZLE2_INTRO;
          }

          setStressLevel(stressForStageRecord(st));

          setStageActive(STAGE_NUM, storedUser);
          if (!cancelled) {
            setDbReady(true);
            setPhase(resumePhase);
          }
          return;
        }

        setStageActive(STAGE_NUM, storedUser);
        if (!cancelled) {
          setDbReady(true);
          setPhase(PHASE.INTRO_STORY);
        }
      } catch (err) {
        console.error("loadStageFromDb:", err);
        if (!cancelled) {
          setDbReady(true);
          setPhase(PHASE.INTRO_STORY);
        }
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, isDemoBypass, storedUser, navigate]);

  const saveLevelProgress = useCallback(
    async (levelNumber, score, answers, adventureScoreSoFar) => {
      const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      if (isDemoBypass || !backendBase || !adminToken || !stageId) return null;

      const timeStr = formatSecondsToClock(elapsedRef.current);
      const total_score = otherStagesTotalRef.current + adventureScoreSoFar;

      const saveResult = await saveGameStageAndReport({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId,
        stageNumber: levelNumber,
        score,
        answers: {
          level: levelNumber,
          puzzle:
            levelNumber === GAME_LEVEL.TASK_TRIAGE
              ? "task_triage"
              : levelNumber === GAME_LEVEL.DESK_ORGANIZE
                ? "desk_organize"
                : "inbox_triage",
          data: answers,
        },
        time: timeStr,
        total_score,
      });

      if (saveResult.total_score != null) {
        setDisplayTotalScore(saveResult.total_score);
      }
      return saveResult;
    },
    [stageRowId, isDemoBypass, backendBase, adminToken, storedUser]
  );

  const finishStage = useCallback(
    async (score) => {
      if (submitting || submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);

      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;
      const timeStr = formatSecondsToClock(elapsedRef.current);
      const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      const finalTotal = otherStagesTotalRef.current + score;

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const saveResult = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          time: timeStr,
          total_score: finalTotal,
        });
        if (saveResult.total_score != null) {
          setDisplayTotalScore(saveResult.total_score);
        }
      }

      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);

      await showStageCompleteCelebration({
        stageNumber: STAGE_NUM,
        skip: false,
        score,
      });

      if (isDemoBypass) {
        window.location.href =
          String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        return;
      }
      navigateAfterCelebration(navigate, "/stage2");
    },
    [
      submitting,
      storedUser,
      isDemoBypass,
      backendBase,
      adminToken,
      stageRowId,
      navigate,
    ]
  );

  const handlePuzzle1Success = useCallback(
    async (assignments) => {
      const adventureScore = STAGE1_PUZZLE_POINTS;
      setStage1Points(adventureScore);
      await saveLevelProgress(
        GAME_LEVEL.TASK_TRIAGE,
        STAGE1_PUZZLE_POINTS,
        assignments,
        adventureScore
      );
      await fireGameStagePopup({
        title: "Priorities Set!",
        html: `<p>You ranked your tasks based on what matters to you. Stress managed — for now.</p><p><strong>+${STAGE1_PUZZLE_POINTS} points</strong></p>`,
        icon: "success",
        confirmButtonText: "Continue",
        themeData,
      });
      setStressLevel(STAGE1_STRESS.AFTER_LEVEL_1);
      setPhase(PHASE.TRIAGE_LEARNING);
    },
    [themeData, saveLevelProgress]
  );

  const handlePuzzle2Success = useCallback(
    async (placements) => {
      const adventureScore = STAGE1_PUZZLE_POINTS + STAGE1_DESK_PUZZLE_POINTS;
      setStage1Points(adventureScore);
      await saveLevelProgress(
        GAME_LEVEL.DESK_ORGANIZE,
        STAGE1_DESK_PUZZLE_POINTS,
        placements,
        adventureScore
      );
      await fireGameStagePopup({
        title: "Workspace Organized!",
        html: `<p>You found the Meeting Room Access Card. Your desk — and your focus — are back on track.</p><p><strong>+${STAGE1_DESK_PUZZLE_POINTS} points</strong></p>`,
        icon: "success",
        confirmButtonText: "Continue",
        themeData,
      });
      setStressLevel(STAGE1_STRESS.AFTER_LEVEL_2);
      setPhase(PHASE.ORG_LEARNING);
    },
    [themeData, saveLevelProgress]
  );

  const handlePuzzle3Success = useCallback(
    async (assignments) => {
      const adventureScore =
        STAGE1_PUZZLE_POINTS + STAGE1_DESK_PUZZLE_POINTS + STAGE1_INBOX_PUZZLE_POINTS;
      setStage1Points(adventureScore);
      await saveLevelProgress(
        GAME_LEVEL.INBOX_TRIAGE,
        STAGE1_INBOX_PUZZLE_POINTS,
        assignments,
        adventureScore
      );
      await fireGameStagePopup({
        title: "Inbox Sorted!",
        html: `<p>You separated signal from noise. Your inbox is manageable again.</p><p><strong>+${STAGE1_INBOX_PUZZLE_POINTS} points</strong></p>`,
        icon: "success",
        confirmButtonText: "Continue",
        themeData,
      });
      setStressLevel(STAGE1_STRESS.FINAL);
      setPhase(PHASE.INBOX_LEARNING);
    },
    [themeData, saveLevelProgress]
  );

  const handleEnterMeetingRoom = useCallback(async () => {
    const totalScore =
      STAGE1_PUZZLE_POINTS + STAGE1_DESK_PUZZLE_POINTS + STAGE1_INBOX_PUZZLE_POINTS;
    sessionStorage.setItem(STAGE2_MEETING_ROOM_KEY, "1");
    await finishStage(totalScore);
  }, [finishStage]);

  const handleNotificationClick = useCallback(async () => {
    if (isDemoBypass && !demoRedirectTriggeredRef.current) {
      demoRedirectTriggeredRef.current = true;
      await fireGameStagePopup({
        title: "Thank you for playing",
        html: "<p>Subscribe to any PLAN to play with your peers.</p>",
        icon: "info",
        confirmButtonText: "OK",
        themeData,
      });
      window.location.assign("https://extramileplay.com/plans");
      return;
    }
    setPhase(PHASE.DESK_INTRO);
  }, [isDemoBypass, themeData]);

  const helpPuzzleKey = useMemo(() => {
    if (phase === PHASE.PUZZLE2) return "desk";
    if (phase === PHASE.PUZZLE3) return "inbox";
    if (phase === PHASE.PUZZLE) return "triage";
    return null;
  }, [phase]);

  const showHelp = Boolean(helpPuzzleKey);

  const handleHowToPlay = useCallback(() => {
    if (!helpPuzzleKey) return;
    fireEscapeHowToPlay({
      stageNumber: STAGE_NUM,
      puzzleKey: helpPuzzleKey,
      themeData,
    });
  }, [helpPuzzleKey, themeData]);

  if (phase === PHASE.LOADING || !dbReady) {
    return (
      <div className="stage1-escape">
        <p className="stage1-escape__loading">Loading game…</p>
      </div>
    );
  }

  return (
    <div
      className={`stage1-escape${
        phase === PHASE.PUZZLE || phase === PHASE.PUZZLE3
          ? " stage1-escape--puzzle"
          : ""
      }${phase === PHASE.PUZZLE2 ? " stage1-escape--desk-puzzle" : ""}`}
    >
      <div className="stage-escape-hud">
        {showHelp ? <StageHelpButton onClick={handleHowToPlay} /> : null}
        <StageTimer timeLabel={formatSecondsToClock(elapsedSec)} />
      </div>

      {showStressMeter ? (
        <StressMeter
          value={stressLevel}
          variant={isMobilePortrait ? "horizontal" : "vertical"}
          elevated={
            phase === PHASE.DESK_INTRO ||
            phase === PHASE.PUZZLE ||
            phase === PHASE.PUZZLE2 ||
            phase === PHASE.PROGRESS
          }
        />
      ) : null}

      {phase === PHASE.INTRO_STORY ? (
        <IntroStoryModal onContinue={() => setPhase(PHASE.NOTIFICATION)} />
      ) : null}

      {phase === PHASE.NOTIFICATION ? (
        <NotificationBlink onClick={handleNotificationClick} />
      ) : null}

      {phase === PHASE.DESK_INTRO ? (
        <DeskIntroModal onContinue={() => setPhase(PHASE.PUZZLE)} />
      ) : null}

      {phase === PHASE.PUZZLE ? (
        <div
          className={`stage1-laptop-scene${
            isMobilePortrait
              ? " stage1-laptop-scene--tablet stage1-laptop-scene--tablet-triage"
              : ""
          }`}
        >
          <div className="stage1-laptop-scene__frame">
            <img
              src={isMobilePortrait ? tabletImg : laptopImg}
              alt=""
              className="stage1-laptop-scene__img"
              draggable={false}
            />
            <div className="stage1-laptop-scene__screen">
              <Stage1TaskTriage
                onSubmit={handlePuzzle1Success}
                submitting={submitting}
                isMobilePortrait={isMobilePortrait}
              />
            </div>
          </div>
        </div>
      ) : null}

      {phase === PHASE.TRIAGE_LEARNING ? (
        <TriageLearningModal onContinue={() => setPhase(PHASE.PUZZLE2_INTRO)} />
      ) : null}

      {phase === PHASE.PUZZLE2_INTRO ? (
        <Puzzle2IntroModal onContinue={() => setPhase(PHASE.PUZZLE2)} />
      ) : null}

      {phase === PHASE.PUZZLE2 ? (
        <Stage1DeskOrganize onComplete={handlePuzzle2Success} submitting={submitting} />
      ) : null}

      {phase === PHASE.ORG_LEARNING ? (
        <OrgLearningModal onContinue={() => setPhase(PHASE.PUZZLE3_INTRO)} />
      ) : null}

      {phase === PHASE.PUZZLE3_INTRO ? (
        <Puzzle3IntroModal onContinue={() => setPhase(PHASE.PUZZLE3)} />
      ) : null}

      {phase === PHASE.PUZZLE3 ? (
        <div
          className={`stage1-laptop-scene${
            isMobilePortrait
              ? " stage1-laptop-scene--tablet stage1-laptop-scene--tablet-inbox"
              : ""
          }`}
        >
          <div className="stage1-laptop-scene__frame">
            <img
              src={isMobilePortrait ? tabletImg : laptopImg}
              alt=""
              className="stage1-laptop-scene__img"
              draggable={false}
            />
            <div className="stage1-laptop-scene__screen">
              <Stage1InboxTriage
                onSubmit={handlePuzzle3Success}
                submitting={submitting}
                isMobilePortrait={isMobilePortrait}
              />
            </div>
          </div>
        </div>
      ) : null}

      {phase === PHASE.INBOX_LEARNING ? (
        <InboxLearningModal onContinue={() => setPhase(PHASE.PROGRESS)} />
      ) : null}

      {phase === PHASE.PROGRESS ? (
        <ProgressModal onContinue={handleEnterMeetingRoom} />
      ) : null}
    </div>
  );
}
